import { useState, Fragment } from "react";
import styled from "styled-components";

import AttitudesVisual from "../../svg/layout_dark_v2_info_attitudes.svg";
import MajorCogFuncVisual from "../../svg/layout_dark_v2_info_major_cogfunc.svg";
import EightCogFuncVisual from "../../svg/layout_dark_v2_info_eight_cogfunc.svg";
import FuncStackVisual from "../../svg/layout_dark_v2_info_funcstack.svg";
import arrowDown from "../../svg/down-arrow-white.svg";
import { arc } from "d3-shape";

import lostkiteLogo from "../../images/logo_thelostkite.png";
import jrLogo from "../../images/logo_jr.png";

const Container = styled.div`
  padding: 20px;
  text-align: left;
`;

const MenuContainer = styled.div`
  margin: 60px auto;
`;

const VisualImg = styled.img`
  max-width: 240px;
  height: auto;
  padding: 10px;
`;

const Section = styled.section`
  margin-bottom: 40px;

  .visual-explainer-container {
    text-align: center;
  }
`;

const SubChapterContainer = styled.div`
  .part-title {
    hr {
      margin-top: 20px;
      border-top: 1px solid #fff;
      opacity: 0.25;
    }
    h4 {
      margin-top: 0;
    }
  }

  .part-content {
    .subpart {
      .subpart-title {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        margin-top: 30px;

        h5 {
          margin: 0;
        }
      }
    }
  }
`;

const DropdownImg = styled.img`
  width: 10px;
  height: 10px;
  display: inline;

  transform: ${({ isDropdownShown }) =>
    isDropdownShown ? `rotate(180deg)` : `rotate(0deg)`};
  transition: transform 0.25s ease;
`;

const SubpartContainer = styled.div`
  cursor: pointer;
`;

const FooterContainer = styled.div`
  margin-bottom: 160px;

  a {
    color: #fff;
    opacity: 0.75;

    &:hover {
      opacity: 0.95;
    }
  }

  .sources-container {
    margin-bottom: 35px;
    ul li {
      margin-bottom: 20px;
    }
  }

  .link-container {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    margin: 0px 10px 10px 0;
    box-sizing: border-box;
    border-radius: 5px;

    &:hover {
      background-color: #333;
    }

    img {
      display: inline;
      width: 30px;
      height: 30px;
      margin: 8px;
    }

    span {
      margin-left: 10px;
    }
  }
`;

const Subpart = ({
  src,
  subcategory,
  isDropdownShownDefault = true,
  description,
}) => {
  const [isDropdownShown, setIsDropdownShown] = useState(
    isDropdownShownDefault
  );

  function handleDropdown() {
    return setIsDropdownShown(!isDropdownShown);
  }

  return (
    <SubpartContainer onClick={handleDropdown}>
      <div className="subpart-title">
        <h5>{subcategory}</h5>
        <DropdownImg src={src} isDropdownShown={isDropdownShown} />
      </div>
      {description && isDropdownShown && <p>{description}</p>}
    </SubpartContainer>
  );
};

const Content = ({ menu, content, footer }) => {
  const visual = (v) =>
    (v === "attitudes" && AttitudesVisual) ||
    (v === "major-cogfunc" && MajorCogFuncVisual) ||
    (v === "eight-cogfunc" && EightCogFuncVisual) ||
    (v === "func-stack" && FuncStackVisual);

  const getLogo = (img) =>
    (img === "logo_jr.png" && jrLogo) ||
    (img === "logo_thelostkite.png" && lostkiteLogo);

  return (
    <Container>
      <MenuContainer className="menu-container">
        <h2>{menu.title}</h2>
        {menu.description && menu.description.map((d) => <p>{d}</p>)}
        <p>{menu.list.title}</p>
        {menu.list.items &&
          menu.list.items.map((item) => (
            <div className="chapter">
              <a>
                <p>{item.title}</p>
              </a>
            </div>
          ))}
      </MenuContainer>
      <hr />
      <div className="content-container">
        {content &&
          content.map((chap) => (
            <Section>
              <hr />
              <h2>{chap.title}</h2>
              <h3>{chap.subtitle}</h3>
              {Array.isArray(chap.description) ? (
                chap.description.map((desc) => <p>{desc}</p>)
              ) : (
                <p>{chap.description}</p>
              )}
              <div className="visual-explainer-container">
                <VisualImg
                  className="visual-explainer"
                  src={visual(chap.visual)}
                  alt={chap.visual}
                />
              </div>
              <div className="sub-section">
                {chap.parts &&
                  chap.parts.map((part) => (
                    <SubChapterContainer>
                      <div className="part-title">
                        <hr />
                        <h4>{part.category}</h4>
                      </div>
                      <p>{part.description}</p>
                      <div className="part-content">
                        {part.subparts &&
                          part.subparts.map((sp) => (
                            <div className="subpart">
                              <Subpart
                                src={arrowDown}
                                className="subpart-title"
                                subcategory={sp.subcategory}
                                isDropdownShownDefault={
                                  sp.isDropdownShownDefault
                                }
                                description={sp.description}
                              />
                            </div>
                          ))}
                      </div>
                    </SubChapterContainer>
                  ))}
              </div>
            </Section>
          ))}
      </div>
      <hr />
      <hr />
      <FooterContainer className="footer-container">
        <div className="sources-container">
          <h3>Sources:</h3>
          <ul>
            {footer.sources.map((src) => (
              <li>
                {src.source} - <a href={src.url}>{src.article}</a>
              </li>
            ))}
          </ul>
        </div>
        <div className="byline">
          <h3>See more interactives like this:</h3>
          {footer.links.map((link) => (
            <a href={link.url}>
              <div className="link-container">
                <img src={getLogo(link.img)} />
                <span>{link.site}</span>
              </div>
            </a>
          ))}
        </div>
      </FooterContainer>
    </Container>
  );
};

export default Content;
